"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { Header } from "@/components/header";
import { HeroSection } from "@/components/hero-section";
import { useInView } from "@/hooks/useInView";
import { useLanguage } from "@/context/LanguageContext";
import { blogsApi, contentInfoApi } from "@/lib/api";
import { getImageUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import dynamic from "next/dynamic";

// Lazy load Footer
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
    loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
    loading: () => null,
    ssr: false,
});

interface Blog {
    id_blog: string;
    title: string;
    slug: string;
    header_img: string | null;
    abstract: string | null;
    content: string;
    tags: string | null;
    is_active: boolean;
    published_at: string | null;
    created_at: string;
    updated_at: string;
    category_ref?: {
        id_category: string;
        name_category: string;
    } | null;
    team?: {
        id_team: string;
        name_team: string;
    } | null;
}

interface PaginatedBlogsResponse {
    data: Blog[];
    total: number;
    skip: number;
    limit: number;
}

interface ContentInfo {
    id_content: string;
    content_title: string;
    content_description?: string | null;
    type?: string | null;
}

export default function Blog() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { language } = useLanguage();

    // Initialize state from URL params
    const [currentPage, setCurrentPage] = useState(() => {
        const page = searchParams?.get('page');
        return page ? parseInt(page, 10) : 1;
    });
    const [searchQuery, setSearchQuery] = useState(() => searchParams?.get('search') || "");
    const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams?.get('search') || "");
    const [selectedCategory, setSelectedCategory] = useState(() => searchParams?.get('category') || "all");
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [startDate, setStartDate] = useState(() => searchParams?.get('start_date') || "");
    const [endDate, setEndDate] = useState(() => searchParams?.get('end_date') || "");
    const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
    // const [postsRef] = useInView<HTMLDivElement>({ threshold: 0.1 });
    const [postsRef, postsInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

    // API state
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [totalBlogs, setTotalBlogs] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
    const [heroContent, setHeroContent] = useState<ContentInfo | null>(null);

    const datePickerRef = useRef<HTMLDivElement>(null);
    const categoryRef = useRef<HTMLDivElement>(null);

    const ITEMS_PER_PAGE = 4;


    const labels = {
        en: {
            allDates: "All Dates",
            dateRange: "Date Range",
            startDate: "Start Date",
            endDate: "End Date",
            category: "Category",
            allCategories: "All Categories",
            findBlog: "Find blog",
            clear: "Clear",
        },
        id: {
            allDates: "Semua Tanggal",
            dateRange: "Rentang Tanggal",
            startDate: "Tanggal Mulai",
            endDate: "Tanggal Selesai",
            category: "Kategori",
            allCategories: "Semua Kategori",
            findBlog: "Cari blog",
            clear: "Bersihkan",
        },
    };

    const t = labels[language];

    useEffect(() => {
        let isMounted = true;

        const fetchHeroContent = async () => {
            try {
                const response = await contentInfoApi.getAll({ limit: 1, type: "blog", lang: language });
                const data = (response.data?.data || response.data) as ContentInfo[];

                if (!isMounted) return;
                setHeroContent(data?.[0] || null);
            } catch (err) {
                if (isMounted) setHeroContent(null);
                console.log("Error fetching blogs hero content:", err);
            }
        };

        fetchHeroContent();
        return () => {
            isMounted = false;
        };
    }, [language]);

    // Update URL when filters change
    useEffect(() => {
        const params = new URLSearchParams();

        if (currentPage > 1) params.set('page', currentPage.toString());
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (selectedCategory !== 'all') params.set('category', selectedCategory);
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);

        const queryString = params.toString();
        const newUrl = queryString ? `/blogs?${queryString}` : '/blogs';

        // Update URL without triggering navigation
        window.history.replaceState(null, '', newUrl);
    }, [currentPage, debouncedSearch, selectedCategory, startDate, endDate]);

    // Debounce search input (300ms delay)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1); // Reset to first page on search
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch blogs from API
    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                setLoading(true);
                setError(null);

                const params: {
                    skip: number;
                    limit: number;
                    is_active: boolean;
                    search?: string;
                    category_id?: string;
                    start_date?: string;
                    end_date?: string;
                    sort_by?: string;
                    sort_order?: string;
                    lang?: string;
                } = {
                    skip: (currentPage - 1) * ITEMS_PER_PAGE,
                    limit: ITEMS_PER_PAGE,
                    is_active: true,
                    sort_by: "published_at",
                    sort_order: "desc",
                    lang: language,
                };

                // Add optional filters
                if (debouncedSearch) {
                    params.search = debouncedSearch;
                }
                if (selectedCategory !== "all") {
                    params.category_id = selectedCategory;
                }
                if (startDate) {
                    params.start_date = startDate;
                }
                if (endDate) {
                    params.end_date = endDate;
                }

                const response = await blogsApi.getAll(params);
                const data: PaginatedBlogsResponse = response.data;

                setBlogs(data.data);
                setTotalBlogs(data.total);

                // Extract unique categories for filter dropdown
                const uniqueCategories = new Map<string, { id: string; name: string }>();
                data.data.forEach((blog) => {
                    if (blog.category_ref?.id_category && blog.category_ref?.name_category) {
                        uniqueCategories.set(blog.category_ref.id_category, {
                            id: blog.category_ref.id_category,
                            name: blog.category_ref.name_category,
                        });
                    }
                });
                setCategories(Array.from(uniqueCategories.values()));
            } catch (err: unknown) {
                if (err && typeof err === "object" && "response" in err) {
                    const response = (err as { response?: { data?: { detail?: string } } }).response;
                    setError(response?.data?.detail || "Failed to load blogs. Please try again.");
                } else {
                    setError("Failed to load blogs. Please try again.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, [currentPage, debouncedSearch, selectedCategory, startDate, endDate, language]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
                setIsDatePickerOpen(false);
            }
            if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
                setIsCategoryOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Calculate pagination
    const totalPages = Math.ceil(totalBlogs / ITEMS_PER_PAGE);

    // Pagination display logic
    const pagesToShow = useMemo(() => {
        const pages: number[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, 4, -1, totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, -1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, -1, currentPage - 1, currentPage, currentPage + 1, -1, totalPages);
            }
        }

        return pages;
    }, [currentPage, totalPages]);

    // Handlers
    const handlePageChange = useCallback((page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        // Smooth scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [totalPages]);

    const handleCategoryChange = useCallback((categoryId: string) => {
        setSelectedCategory(categoryId);
        setCurrentPage(1); // Reset to first page
        setIsCategoryOpen(false);
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    const clearDateFilter = useCallback(() => {
        setStartDate("");
        setEndDate("");
        setCurrentPage(1); // Reset to first page
    }, []);

    const clearAllFilters = useCallback(() => {
        setSearchQuery("");
        setDebouncedSearch("");
        setSelectedCategory("all");
        setStartDate("");
        setEndDate("");
        setCurrentPage(1);
    }, []);

    // Format date for display
    const formatDate = (dateString: string | null) => {
        if (!dateString) return "No date";
        const date = new Date(dateString);
        return date.toLocaleDateString(language === "id" ? "id-ID" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Extract tags array from string
    const getTags = (tagsString: string | null): string[] => {
        if (!tagsString) return [];
        return tagsString.split(",").map((tag) => tag.trim());
    };

    const selectedCategoryLabel =
        selectedCategory === "all"
            ? "All Categories"
            : categories.find((category) => category.id === selectedCategory)?.name || "All Categories";

    return (
        <div className="page-layout bg-white">
            <div className="page-content">
                {/* Header */}
                <Header activePage="blog" />

                {/* Hero Section */}
                <HeroSection
                    title={heroContent?.content_title || "Our Blogs"}
                    subtitle={heroContent?.content_description || "Insights, updates, and stories from our team"}
                />

                {/* Main Content */}
                <section className="py-12 px-8 lg:px-16 bg-white">
                    <div className="max-w-7xl mx-auto">
                        {/* Search and Filter Section */}
                        <div
                            ref={contentRef}
                            className={`relative z-30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 reveal ${contentInView ? "is-visible" : ""}`}
                        >
                            {/* Search Input */}
                            <div className="relative w-full md:w-auto">
                                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <Input
                                    type="text"
                                    placeholder={t.findBlog}
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="pl-10 w-full md:w-64 bg-white"
                                />
                            </div>

                            {/* Filter Buttons */}
                            <div className="flex gap-3">
                                {/* Date Range Filter */}
                                <div className="relative" ref={datePickerRef}>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setIsDatePickerOpen(!isDatePickerOpen);
                                            setIsCategoryOpen(false);
                                        }}
                                        className="flex items-center gap-2 border-gray-300 hover:bg-gray-50 bg-white"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        {startDate || endDate ? t.dateRange : t.allDates}
                                    </Button>
                                    {isDatePickerOpen && (
                                        <div className="absolute left-0 mt-2 w-72 bg-white border border-gray-300 rounded-md shadow-lg z-999 p-4">
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {t.startDate}
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={startDate}
                                                        onChange={(e) => setStartDate(e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {t.endDate}
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={endDate}
                                                        onChange={(e) => setEndDate(e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        onClick={clearDateFilter}
                                                        className="flex-1"
                                                    >
                                                        {t.clear}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Category Filter */}
                                <div className="relative" ref={categoryRef}>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setIsCategoryOpen(!isCategoryOpen);
                                            setIsDatePickerOpen(false);
                                        }}
                                        className="flex items-center gap-2 border-gray-300 hover:bg-gray-50 bg-white"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                        </svg>
                                        {selectedCategory === "all" ? t.category : selectedCategoryLabel}
                                    </Button>
                                    {isCategoryOpen && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-999">
                                            <button
                                                onClick={() => handleCategoryChange("all")}
                                                className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-black"
                                            >
                                                {t.allCategories}
                                            </button>
                                            {categories.map((category) => (
                                                <button
                                                    key={category.id}
                                                    onClick={() => handleCategoryChange(category.id)}
                                                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-black"
                                                >
                                                    {category.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Active Filters Display */}
                        {/* {(debouncedSearch || selectedCategory !== 'all' || startDate || endDate) && (
                        <div className="mb-6 flex flex-wrap items-center gap-2">
                            <span className="text-sm text-gray-600 font-medium">Active filters:</span>
                            {debouncedSearch && (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                    Search: &ldquo;{debouncedSearch}&rdquo;
                                    <button onClick={() => { setSearchQuery(""); setDebouncedSearch(""); }} className="hover:text-blue-900">
                                        ×
                                    </button>
                                </span>
                            )}
                            {selectedCategory !== 'all' && (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                    Category: {selectedCategoryLabel}
                                    <button onClick={() => setSelectedCategory('all')} className="hover:text-blue-900">
                                        ×
                                    </button>
                                </span>
                            )}
                            {(startDate || endDate) && (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                    Date: {startDate || 'Start'} - {endDate || 'End'}
                                    <button onClick={clearDateFilter} className="hover:text-blue-900">
                                        ×
                                    </button>
                                </span>
                            )}
                            <button
                                onClick={clearAllFilters}
                                className="text-sm text-gray-600 hover:text-gray-900 underline"
                            >
                                Clear all
                            </button>
                        </div>
                    )} */}

                        {/* Loading State */}
                        {loading && (
                            <div className="flex justify-center items-center py-20">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-8">
                                <p className="font-medium">Error loading blogs</p>
                                <p className="text-sm">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="mt-2 text-sm underline hover:no-underline"
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {/* Empty State */}
                        {!loading && !error && blogs.length === 0 && (
                            <div className="text-center py-20">
                                <p className="text-gray-500 text-lg">
                                    {debouncedSearch || selectedCategory !== 'all' || startDate || endDate
                                        ? "No blogs found matching your criteria."
                                        : "No blogs available at the moment"}
                                </p>
                                {(debouncedSearch || selectedCategory !== 'all' || startDate || endDate) && (
                                    <button
                                        onClick={clearAllFilters}
                                        className="mt-4 px-6 py-2 bg-[#0C1A2E] text-white rounded-lg hover:bg-[#285494] transition-colors"
                                    >
                                        Clear all filters
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Blog Posts Grid */}
                        {/* Blog Posts Section (with reveal wrapper like research/page.tsx) */}
                        <div
                            ref={postsRef}
                            className={`relative z-0 reveal ${postsInView ? "is-visible reveal-delay-200" : ""
                                }`}
                        >
                            {!loading && !error && blogs.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                                    {blogs.map((blog) => (
                                        <div
                                            key={blog.id_blog}
                                            onClick={() => router.push(`/blogs/${blog.id_blog}`)}
                                            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                                        >
                                            {/* Image */}
                                            <div className="relative w-full h-48 bg-gray-200">
                                                <Image src={getImageUrl(blog.header_img)}
                                                    alt={blog.title}
                                                    fill
                                                    className="object-cover"
                                                    loading="lazy" />
                                            </div>

                                            {/* Content */}
                                            <div className="p-5">
                                                <div className="text-sm text-black mb-2">
                                                    {blog.category_ref?.name_category || "General"} •{" "}
                                                    {formatDate(blog.published_at)}
                                                </div>

                                                <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                                                    {blog.title}
                                                </h2>

                                                <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                                                    {blog.abstract || "No description available"}
                                                </p>

                                                {blog.tags && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {getTags(blog.tags).slice(0, 3).map((tag, index) => (
                                                            <span
                                                                key={index}
                                                                className="px-3 py-1 text-xs border border-black rounded-full text-black bg-white hover:bg-gray-50 transition-colors"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>


                        {/* Pagination */}
                        {!loading && !error && totalPages > 1 && (
                            <div className="flex justify-end items-center gap-2 mt-12">
                                {/* Prev */}
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>

                                {/* Pages */}
                                {pagesToShow.map((page, idx) => (
                                    <button
                                        key={`${page}-${idx}`}
                                        onClick={() => typeof page === "number" && handlePageChange(page)}
                                        disabled={page === -1}
                                        className={`px-4 py-2 rounded-md border transition-colors ${page === currentPage
                                            ? "bg-[#0C1A2E] text-white border-[#0C1A2E]"
                                            : page === -1
                                                ? "border-gray-300 text-gray-400 cursor-default px-2"
                                                : "border-gray-300 text-gray-700 hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50"
                                            }`}
                                    >
                                        {page === -1 ? "..." : page}
                                    </button>
                                ))}

                                {/* Next */}
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        )}

                    </div>
                </section>
            </div>

            {/* Footer */}
            <Footer />
            {/* Chat Widget */}
            <ChatWidget />
        </div>
    );
}

